import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';

import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import type { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { Box, MenuItem, MenuList, useTheme } from '@material-ui/core';
import { HiOutlineTrash } from 'react-icons/hi';

import { formatTableDate } from '@vyce/core/src/utils/dates';
import {
  ListEmployeeRequestData,
  RemoveEmployeeFromModuleRequest,
  RemoveEmployeeRequest,
} from '@vyce/core/src/api/types';
import {
  Employee,
  FilterSection,
  Nationality,
  PayrollUser,
  PaySchedule,
  Tag,
  TagGroups,
  FilterOption,
} from '@vyce/core/src/types';
import { PAY_PERMISSIONS } from '@vyce/core/src/types';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { useHorizontalScrollStyles } from '@vyce/core/src/styles';
import { DeviceContext } from '@vyce/core/src/contexts';
import {
  BUSINESS_TYPES,
  DOCUMENT_STATUSES_ARR,
  GRID_PAGE_SIZE,
  RTW_FILTER_OPTIONS,
  SUBCONTRACTOR_TYPES,
  TABLE_OFFSET_DELAY,
  TAXATION_STATUSES,
} from '@vyce/core/src/constants';
import {
  AppA,
  AppDataGridWithSavedPage,
  ConfirmDialog,
  FilterSystem,
  GridActions,
  GridCellWithAvatar,
  IDStatus,
  RTWStatus,
} from '@vyce/core/src/components';
import { getAvatar } from '@vyce/core/src/utils/getAvatar';
import { formatSortModel } from '@vyce/core/src/utils/sorting';
import { fetchNationalitiesRequest, fetchTagsRequest } from '@vyce/core/src/api/handbook';
import { getBooleanFilterValue, isNil } from '@vyce/core/src/utils';
import { AppSearchInput } from '@vyce/core/src/components/inputs';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { useDebounceValue } from '@vyce/core/src/hooks/useDebounceValue';
import { useTable } from '@vyce/core/src/hooks/useTable';

import { monochrome } from '../../../theme/styles';

interface Props {
  removeEmployeeFromPayScheduleRequest: (data: RemoveEmployeeRequest) => void;
  removeEmployeeFromModuleRequest: (data: RemoveEmployeeFromModuleRequest) => Promise<any>;
  getEmployeeListRequest: Function;
  paySchedules?: PaySchedule[];
  companyId?: string;
  token: string;
  children: React.ReactElement;
  withProfileButton?: boolean;
  height?: string;
  showPaySchedule?: boolean;
}

const defaultSortModel: GridSortModel = [{ field: 'created_at', sort: 'desc' }];

export const EmployeeList: React.FC<Props> = ({
  removeEmployeeFromPayScheduleRequest,
  removeEmployeeFromModuleRequest,
  getEmployeeListRequest,
  companyId,
  paySchedules = [],
  token,
  children,
  withProfileButton,
  height = 'calc(100vh - 240px)',
  showPaySchedule = true,
}) => {
  const theme = useTheme();

  const { handleServerError } = useContext(NotificationContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<Partial<ListEmployeeRequestData>>();
  const [selectedEmployee, setSelectedEmployee] = useState<Employee>();
  const [selectedPayScheduleId, setSelectedPayScheduleId] = useState<string>();
  const [employees, setEmployees] = useState<PayrollUser[]>([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [roles, setRoles] = useState<Tag[]>([]);
  const [nationalities, setNationalities] = useState<Nationality[]>([]);
  const history = useHistory();
  const [closeGridAction, setCloseGridAction] = useState<boolean>(false);
  const horizontalScrollClasses = useHorizontalScrollStyles();
  const { isMobile } = useContext(DeviceContext);
  const [deletingEmployee, setDeletingEmployee] = useState(false);

  const {
    sortModel,
    offset,
    substring,
    total,
    setTotal,
    setOffset,
    handleSortModelChange,
    handlePageChange,
    handleSearchChange,
  } = useTable({ defaultSortModel });

  const dOffset = useDebounceValue(offset, TABLE_OFFSET_DELAY);

  const enrichedEmployees = useMemo(
    () => employees.map((item, i) => ({ ...item, id: i + offset })),
    [employees]
  );

  const textColor = theme.palette.type === 'dark' ? monochrome.medium : monochrome.mediumdark;
  const { canAnywhere } = usePermissions();
  // The list spans every pay schedule, so the right on any one of them counts.
  const canManageEmployees = canAnywhere(PAY_PERMISSIONS.MANAGE_EMPLOYEES);

  const resetCloseStatus = useCallback(() => {
    setCloseGridAction(true);
    setTimeout(() => setCloseGridAction(false), 100);
  }, []);

  const defaultPayFilters: FilterOption[] = useMemo(
    () => [
      {
        type: 'select',
        multiple: false,
        label: 'Pay Schedule',
        field: 'payScheduleName',
        values: ['', ...paySchedules.map(item => item?.name)],
        defaultValue: '',
      },
      {
        type: 'select',
        multiple: true,
        label: 'Pay Scheme',
        field: 'pay_schemes',
        values: [
          SUBCONTRACTOR_TYPES.CIS_SELF_EMPLOYED.toUpperCase(),
          SUBCONTRACTOR_TYPES.SELF_EMPLOYED.toUpperCase(),
          SUBCONTRACTOR_TYPES.PAYE.toUpperCase(),
        ],
        defaultValue: [],
      },
      {
        type: 'select',
        multiple: false,
        label: 'Taxation Statuses',
        field: 'taxation_statuses',
        values: [
          '',
          { name: 'CIS 20%', value: TAXATION_STATUSES.CIS20 },
          { name: 'CIS 30%', value: TAXATION_STATUSES.CIS30 },
          { name: 'CIS Gross', value: TAXATION_STATUSES.CIS_GROSS },
          { name: 'Self-employed Gross', value: TAXATION_STATUSES.SELF_EMPLOYED_GROSS },
          { name: 'Gross', value: TAXATION_STATUSES.GROSS },
        ],
        defaultValue: '',
      },
      {
        type: 'autocomplete',
        multiple: true,
        label: 'Business Types',
        field: 'business_types',
        values: [BUSINESS_TYPES.COMPANY, BUSINESS_TYPES.SOLE_TRADER],
        defaultValue: [],
      },
    ],
    [paySchedules]
  );

  const filtersSections: FilterSection[] = useMemo(
    () => [
      {
        title: 'Checks',
        expanded: true,
        filters: [
          {
            type: 'select',
            multiple: false,
            label: 'ID Verified',
            field: 'id_verified',
            values: ['', 'Verified', 'Not Verified'],
            defaultValue: '',
          },
          {
            type: 'select',
            multiple: false,
            label: 'ID Status',
            field: 'id_status',
            values: ['', ...DOCUMENT_STATUSES_ARR],
            defaultValue: '',
          },
          {
            type: 'select',
            multiple: false,
            label: 'RTW Status',
            field: 'rtw_status',
            values: ['', ...RTW_FILTER_OPTIONS],
            defaultValue: '',
          },
          {
            type: 'select',
            multiple: false,
            label: 'VAT Registered',
            field: 'vat_registered',
            values: ['', 'VAT Registered', 'Not VAT Registered'],
            defaultValue: '',
          },
        ],
      },
      {
        title: 'Payroll Types',
        expanded: true,
        filters: showPaySchedule
          ? defaultPayFilters
          : defaultPayFilters.filter(filter => filter.field !== 'payScheduleName'),
      },
      {
        title: 'Worker Profiles',
        expanded: false,
        filters: [
          {
            type: 'autocomplete',
            multiple: true,
            label: 'Job Role',
            field: 'job_roles',
            values: roles,
            defaultValue: [],
          },
          {
            type: 'autocomplete',
            multiple: true,
            label: 'Nationality',
            field: 'nationalities',
            values: nationalities,
            defaultValue: [],
          },
        ],
      },
    ],
    [showPaySchedule, defaultPayFilters, roles, nationalities]
  );

  const columns: GridColDef[] = useMemo(() => {
    const defaultColumns: GridColDef[] = [
      {
        field: 'first_name',
        headerName: 'Name',
        flex: 0.2,
        minWidth: 140,
        disableColumnMenu: true,
        renderCell: params => (
          <GridCellWithAvatar
            avatarUrl={params.row.photo}
            avatarPlaceholder={getAvatar(params.row.gender)}
            name={`${params.row.first_name} ${params.row.last_name}`}
            link={`${history.location.pathname}/${params.row?.first_name}_${params.row?.user_id}`}
          />
        ),
      },
      {
        field: 'ni_number',
        headerName: 'Ref #',
        flex: 0.1,
        width: 95,
        disableColumnMenu: true,
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 0.15,
        minWidth: 240,
        disableColumnMenu: true,
        renderCell: params => <AppA content={params.row.email} href={`mailto: ${params.row.email}`} />,
      },
      {
        field: 'phone',
        headerName: 'Phone Number',
        flex: 0.1,
        disableColumnMenu: true,
        minWidth: 150,
        renderCell: params => <AppA content={params.row.phone} href={`tel: ${params.row.phone}`} />,
      },
      {
        field: 'created_at',
        headerName: 'Registration Date',
        flex: 0.1,
        disableColumnMenu: true,
        valueGetter: params => formatTableDate(params.row?.created_at),
        minWidth: 140,
      },
      {
        field: 'pay_schedule',
        headerName: 'Pay Schedule',
        valueGetter: params => params.row.pay_schedule?.name,
        flex: 0.1,
        hideSortIcons: true,
        sortable: false,
        disableColumnMenu: true,
        minWidth: 100,
      },
      {
        field: 'taxation_status',
        headerName: 'Tax Status',
        valueGetter: params => params.row?.taxation_status?.toUpperCase(),
        flex: 0.1,
        disableColumnMenu: true,
        minWidth: 120,
      },
      {
        field: 'pay_scheme',
        headerName: 'Type',
        valueGetter: params => params.row?.pay_scheme?.toUpperCase(),
        flex: 0.05,
        disableColumnMenu: true,
        minWidth: 80,
      },
      {
        field: 'id_verified',
        headerName: 'ID verified',
        flex: 0.1,
        renderCell: params => (
          <IDStatus
            idExpiring={params.row.id_expiring}
            verified={params.row.id_verified}
            status={params.row.id_status}
          />
        ),
        disableColumnMenu: true,
        width: 105,
      },
      {
        field: 'rtw_verified',
        headerName: 'RTW',
        flex: 0.1,
        renderCell: params => (
          <RTWStatus verified={params.row.rtw_verified} idExpiring={params.row.rtw_expiring} />
        ),
        disableColumnMenu: true,
        width: 120,
      },
      {
        field: '',
        headerName: 'Actions',
        width: 80,
        hideSortIcons: true,
        sortable: false,
        disableColumnMenu: true,
        renderCell: params =>
          !canManageEmployees ? null : (
            <Box display="flex" width="100%">
              <GridActions close={closeGridAction}>
                <MenuList>
                  {showPaySchedule && (
                    <MenuItem
                      onClick={() => {
                        setSelectedEmployee(params.row as Employee);
                        setSelectedPayScheduleId(params.row.pay_schedule?.pay_schedule_id);
                        setOpenConfirmDialog(true);
                        resetCloseStatus();
                      }}>
                      <Box display="flex" alignItems="center" gridGap={8} color={textColor}>
                        <HiOutlineTrash size={14} /> Remove from '{params.row.pay_schedule.name}' pay
                        schedule
                      </Box>
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => {
                      setSelectedEmployee(params.row as Employee);
                      setOpenConfirmDialog(true);
                      setSelectedPayScheduleId(undefined);
                      resetCloseStatus();
                    }}>
                    <Box display="flex" alignItems="center" gridGap={8} color={textColor}>
                      <HiOutlineTrash size={14} /> Remove from Payroll Module
                    </Box>
                  </MenuItem>
                </MenuList>
              </GridActions>
            </Box>
          ),
      },
    ];

    return showPaySchedule
      ? defaultColumns
      : defaultColumns.filter(column => column.field !== 'pay_schedule');
  }, [
    showPaySchedule,
    history.location.pathname,
    textColor,
    closeGridAction,
    resetCloseStatus,
    canManageEmployees,
  ]);

  const fetchJobsAndNationalities = async () => {
    try {
      const resRoles = await fetchTagsRequest({ group: TagGroups.ROLE, short: false });
      const resNationalities = await fetchNationalitiesRequest();
      setRoles(resRoles.data.items);
      setNationalities(resNationalities.data.items);
    } catch (e) {
      console.error(e);
    }
  };

  const getEmployees = useCallback(async () => {
    if (!companyId || isNil(dOffset)) {
      return;
    }

    try {
      setLoading(true);
      const res = await getEmployeeListRequest({
        companyId,
        data: {
          substring,
          offset: dOffset,
          limit: GRID_PAGE_SIZE,
          company_id: companyId,
          order_by: formatSortModel<PayrollUser>(sortModel),
          ...filters,
        },
      });
      setTotal(res.data.count);
      setEmployees(res.data.items);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.error(e);
    }
  }, [companyId, substring, dOffset, sortModel, filters]);

  const deleteEmployee = async () => {
    if (!selectedEmployee) {
      return;
    }
    const targetCompanyId = selectedEmployee.company?.company_id || companyId;
    try {
      setDeletingEmployee(true);
      if (selectedPayScheduleId) {
        await removeEmployeeFromPayScheduleRequest({
          companyId: targetCompanyId,
          userId: selectedEmployee.user_id,
          payScheduleId: selectedPayScheduleId,
        });
      } else {
        await removeEmployeeFromModuleRequest({
          token,
          companyId: targetCompanyId,
          userId: selectedEmployee.user_id,
        });
      }
      getEmployees();
      setSelectedEmployee(undefined);
      setSelectedPayScheduleId(undefined);
      setOpenConfirmDialog(false);
      setDeletingEmployee(false);
    } catch (e) {
      handleServerError(e);
      setDeletingEmployee(false);
    }
  };

  useEffect(() => {
    fetchJobsAndNationalities();
  }, []);

  const handleFilterChange = (newFilters: any) => {
    const formattedFilters = prepareFilters(newFilters);
    setFilters(formattedFilters);
  };

  const prepareFilters = (filters: any): ListEmployeeRequestData => {
    const selectedPaySchedule = showPaySchedule
      ? paySchedules?.find(item => item.name === filters?.payScheduleName)
      : undefined;
    return {
      pay_schedule_id: selectedPaySchedule?.uuid,
      id_verified: getBooleanFilterValue(filters.id_verified),
      rtw_status: filters.rtw_status?.value,
      vat_registered: getBooleanFilterValue(filters.vat_registered),
      id_status: filters.id_status,
      pay_schemes: filters.pay_schemes?.map((item: string) => item.toLowerCase()),
      taxation_statuses: filters.taxation_statuses?.value ? [filters.taxation_statuses.value] : [],
      business_types: filters.business_types?.map((item: string) => item.toLowerCase()),
      job_roles: filters.job_roles?.map((item: Tag) => item.name),
      nationalities: filters.nationalities?.map((item: Nationality) => item.name),
      company_id: companyId,
    };
  };

  useEffect(() => {
    getEmployees();
  }, [getEmployees]);

  return (
    <>
      <Box
        marginBottom={2}
        gridGap={16}
        className={clsx(horizontalScrollClasses.blockWrapper, horizontalScrollClasses.blockWithHideScroll)}>
        <Box>
          <AppSearchInput
            cypressId="search-payroll-users"
            onChange={handleSearchChange}
            isSmall
            smallRounding
            expanded={!isMobile}
          />
        </Box>

        <Box display="flex" gridGap={16}>
          {children}
          <FilterSystem filtersSections={filtersSections} onFiltersChange={handleFilterChange} />
        </Box>
      </Box>

      <AppDataGridWithSavedPage
        noPaper
        rows={enrichedEmployees}
        columns={columns}
        height={height}
        rowCount={total}
        loading={loading}
        pageSize={GRID_PAGE_SIZE}
        paginationMode="server"
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onPageChange={handlePageChange}
        rowsPerPageOptions={[GRID_PAGE_SIZE]}
        disableSelectionOnClick
        unit="users"
        withProfileButton={withProfileButton}
        setOffset={setOffset}
      />

      <ConfirmDialog
        handleClose={() => {
          setOpenConfirmDialog(false);
          setSelectedEmployee(undefined);
          setSelectedPayScheduleId(undefined);
        }}
        buttonMinWidth={181}
        loading={deletingEmployee}
        open={openConfirmDialog}
        confirmText="Yes, remove them"
        cancelText="No, go back"
        title="Before we remove this profile..."
        subtitle="If you remove this profile you will not be able to process payments for this person going forward. Still want to remove them?"
        handleConfirm={deleteEmployee}
      />
    </>
  );
};
